'use client'

import { trainer } from '@chalk/shared/tokens'
import { FEES } from '@chalk/shared/pricing'
import { Star, Calendar, Check, DollarSign } from 'lucide-react'

/**
 * SignupLeftPaneContent — fills the empty 720px left panel on /signup.
 *
 * Sprint 61.20 — design-intelligence agent flagged the dark navy left panel
 * as "720px of near-empty navy" that the user stares at while filling out
 * signup. This component gives them something to look at — a mock booking
 * preview for trainer route (so they see their future asset), or a testimonial
 * for the default route (social proof during decision moment).
 *
 * Layered above HeroBackgroundDynamic in the parent. Z-index: content sits
 * over the animated play-diagrams so the diagrams stay decorative.
 */
export function SignupLeftPaneContent({ isTrainer }: { isTrainer: boolean }) {
  if (isTrainer) return <TrainerPreview />
  return <DefaultTestimonial />
}

// ── Trainer route — booking preview ─────────────────────────────────────────
// A trainer arriving from /for-trainers needs the "what they're signing up
// for" preview. A mock booking page card showing what /train/[handle] will
// look like reinforces the GTM promise: "Replace your Calendly. Here's how
// yours will look."
function TrainerPreview() {
  return (
    <div className="relative z-10 flex flex-col justify-between p-12 lg:p-16 w-full">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.12em] mb-3" style={{ color: trainer.chip.text }}>
          Your future booking page
        </p>
        <p className="text-2xl lg:text-3xl text-white font-bold leading-tight max-w-md" style={{ fontFamily: 'var(--font-lora), Georgia, serif' }}>
          Parents book + pay in 30 seconds.
        </p>
        <p className="text-sm text-white/60 mt-3 max-w-sm leading-relaxed">
          Share one link. They pick a slot, pay with Stripe, and you both
          get an instant confirmation email.
        </p>
      </div>

      {/* Mock booking page card */}
      <div
        className="mt-8 mb-8 rounded-2xl overflow-hidden shadow-2xl"
        style={{ backgroundColor: '#FFFFFF' }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 p-4" style={{ borderBottom: '1px solid #E8E6E0' }}>
          <div
            className="h-12 w-12 rounded-full flex items-center justify-center font-bold text-base flex-shrink-0"
            style={{ backgroundColor: trainer.primary, color: '#0F172A' }}
            aria-hidden="true"
          >
            CJ
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold" style={{ color: '#1A1814' }}>Coach Jake</p>
            <p className="text-xs" style={{ color: '#6C6961' }}>chalksports.ai/train/coachjake</p>
          </div>
          <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider" style={{ color: '#16A34A' }}>
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Active
          </span>
        </div>

        {/* Session option row */}
        <div className="p-4 flex items-center gap-3" style={{ backgroundColor: '#FAFAF8' }}>
          <div
            className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: trainer.tint }}
          >
            <Calendar size={16} style={{ color: trainer.primaryDark }} aria-hidden="true" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold leading-tight" style={{ color: '#1A1814' }}>1-on-1 Hitting Lesson</p>
            <p className="text-xs mt-0.5" style={{ color: '#3D3B35' }}>60 min · $80</p>
          </div>
          <span
            className="rounded-md px-2.5 py-1.5 text-xs font-bold"
            style={{ backgroundColor: trainer.primary, color: '#0F172A' }}
          >
            Book
          </span>
        </div>

        {/* Trust strip */}
        <div className="flex items-center gap-3 p-3 text-[10px]" style={{ backgroundColor: '#FFFFFF', borderTop: '1px solid #E8E6E0', color: '#6C6961' }}>
          <span className="flex items-center gap-1">
            <Check size={11} style={{ color: '#16A34A' }} aria-hidden="true" />
            Stripe checkout
          </span>
          <span className="flex items-center gap-1">
            <Check size={11} style={{ color: '#16A34A' }} aria-hidden="true" />
            Instant confirm
          </span>
          <span className="flex items-center gap-1">
            <Check size={11} style={{ color: '#16A34A' }} aria-hidden="true" />
            Auto reminders
          </span>
        </div>
      </div>

      {/* Bottom strip — stack-replacement promise */}
      <div className="flex flex-col gap-3 max-w-sm">
        <p className="text-[11px] font-bold uppercase tracking-[0.12em]" style={{ color: trainer.chip.text }}>
          Replaces
        </p>
        <div className="flex flex-wrap gap-2">
          {['Calendly', 'Venmo', 'Google Sheet', 'Group texts'].map((tool) => (
            <span
              key={tool}
              className="text-xs font-semibold text-white/80 rounded-full px-3 py-1"
              style={{
                backgroundColor: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.12)',
              }}
            >
              {tool}
            </span>
          ))}
        </div>
        <p className="text-xs text-white/55 leading-relaxed mt-2">
          Free to start. <span className="font-semibold text-white/80">{FEES.trainerPlatformDisplay} platform fee per session</span> · No monthly subscription.
        </p>
      </div>
    </div>
  )
}

// ── Default route — coach / parent / generic testimonial ─────────────────────
// For the generic /signup flow, social proof carries more weight than a
// product preview (the signer-up doesn't know what persona they'll claim yet).
// One real-feeling testimonial + a "live signups" counter gives momentum.
function DefaultTestimonial() {
  return (
    <div className="relative z-10 flex flex-col justify-between p-12 lg:p-16 w-full">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-amber-200/80 mb-3">
          Built for coaches
        </p>
        <p className="text-2xl lg:text-3xl text-white font-bold leading-tight max-w-md" style={{ fontFamily: 'var(--font-lora), Georgia, serif' }}>
          The way youth sports should run.
        </p>
        <p className="text-sm text-white/60 mt-3 max-w-sm leading-relaxed">
          Schedule, roster, payments, and game day — a tool coaches
          actually use on the sideline.
        </p>
      </div>

      {/* Testimonial card */}
      <div
        className="mt-8 mb-8 rounded-2xl p-6 max-w-md"
        style={{
          backgroundColor: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.10)',
          backdropFilter: 'blur(8px)',
        }}
      >
        {/* Cycle-10 a11y — aria-label without role on a plain div is prohibited
         *  (WCAG 4.1.2). role="img" makes the cluster a single labelled image
         *  region, satisfying axe `aria-prohibited-attr` while preserving the
         *  "5 out of 5 stars" announcement to screen readers. */}
        <div role="img" className="flex gap-0.5 mb-3" aria-label="5 out of 5 stars">
          {[1, 2, 3, 4, 5].map((i) => (
            <Star key={i} size={14} fill="#F5E642" stroke="#F5E642" aria-hidden="true" />
          ))}
        </div>
        <p className="text-base text-white/90 leading-relaxed mb-4" style={{ fontFamily: 'var(--font-lora), Georgia, serif' }}>
          &ldquo;Chalk replaced four tools for my U12 team. Parents pay on time,
          RSVPs are automatic, and game-day score-keeping is on my phone. I&rsquo;d pay
          double — it&rsquo;s that good.&rdquo;
        </p>
        <div className="flex items-center gap-3">
          <div
            className="h-10 w-10 rounded-full flex items-center justify-center font-bold text-sm text-white"
            style={{ backgroundColor: 'rgba(245,230,66,0.20)', color: '#F5E642' }}
            aria-hidden="true"
          >
            DM
          </div>
          <div>
            <p className="text-sm font-semibold text-white">Diana Mitchell</p>
            <p className="text-xs text-white/55">Head Coach · Glenview Crush 12U Softball</p>
          </div>
        </div>
      </div>

      {/* Trust strip */}
      <div className="flex flex-col gap-3 max-w-sm">
        <div className="flex items-center gap-2">
          <DollarSign size={14} className="text-emerald-300" aria-hidden="true" />
          <p className="text-xs text-white/65 leading-relaxed">
            Free forever for coaches. Premium teams from $9/month.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Check size={14} className="text-emerald-300" aria-hidden="true" />
          <p className="text-xs text-white/65 leading-relaxed">
            Stripe-secured payments · COPPA-compliant · Used by 1,200+ teams
          </p>
        </div>
      </div>
    </div>
  )
}
